import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trackCustomQuantityEntered } from "@/lib/analytics/segment";
import { deferredFocusRef } from "@/lib/dom";
import { getQuantityError, parseQuantityInput, stripQuantitySeparators } from "@/lib/product/customQuantityValidation";
import { getQuantityBounds } from "@/lib/product/quantities";
import { useProduct } from "@/queries/useProduct";
import { usePdpStore } from "@/stores/usePdpStore";
import ApplyButton from "./ApplyButton";
import { useProductBuilderContext } from "./ProductBuilderContext";
import RequiredIndicator from "./RequiredIndicator";
interface Props {
  prefill: boolean;
}
export default function CustomQuantity({
  prefill
}: Props) {
  const {
    product
  } = useProductBuilderContext();
  const {
    data: productAdmin
  } = useProduct(product.sku);
  const selectedMaterial = usePdpStore(state => state.material);
  const storeQuantity = usePdpStore(state_0 => state_0.quantity);
  const selectQuantity = usePdpStore(state_1 => state_1.selectQuantity);
  const clearValidationError = usePdpStore(state_2 => state_2.clearValidationError);
  const material = selectedMaterial ?? productAdmin?.defaultMaterial;
  const bounds = productAdmin ? getQuantityBounds(productAdmin, material) : {
    minQuantity: 0,
    maxQuantity: 0
  };
  const constraints = {
    ...bounds,
    increment: productAdmin?.customQuantityIncrement ?? 1
  };
  const [localValue, setLocalValue] = useState(prefill ? usePdpStore.getState().quantity?.toString() ?? "" : "");
  const [error, setError] = useState<string>();
  const isApplied = !!localValue && storeQuantity?.toString() === localValue;
  function handleChange(value: string) {
    setLocalValue(stripQuantitySeparators(value));
    setError(undefined);
  }
  function validate() {
    const parsed = parseQuantityInput(localValue);
    if (parsed === undefined) {
      if (localValue.trim()) setError("Please enter a valid quantity");
      return;
    }
    const quantityError = getQuantityError(parsed, constraints);
    if (quantityError) {
      setError(quantityError);
      return;
    }
    selectQuantity(parsed);
    clearValidationError("quantity");
    trackCustomQuantityEntered(parsed);
  }
  return <div className="flex flex-col gap-3 pt-3">
      <p id="custom-quantity-hint" className="text-sm leading-5 text-muted-foreground">
        Enter a quantity between {constraints.minQuantity.toLocaleString()} and{" "}
        {constraints.maxQuantity.toLocaleString()}
        {constraints.increment > 1 && ` (multiples of ${constraints.increment.toLocaleString()})`}
      </p>
      <form onSubmit={e => {
      e.preventDefault();
      validate();
    }} className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor="custom-quantity" className="text-sm leading-5">
            Quantity
            <RequiredIndicator />
          </Label>
          <div className="flex items-center gap-2">
            <Input ref={deferredFocusRef} id="custom-quantity" inputMode="numeric" aria-invalid={!!error || undefined} aria-describedby={error ? "custom-quantity-hint custom-quantity-error" : "custom-quantity-hint"} value={localValue} onChange={e_0 => handleChange(e_0.target.value)} className="max-w-24 text-sm" />
            <ApplyButton isApplied={isApplied} disabled={!localValue.trim()} />
          </div>
        </div>
        {error && <p id="custom-quantity-error" role="alert" className="text-sm text-destructive">
            {error}
          </p>}
      </form>
    </div>;
}